import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Megaphone, Plus, Pencil, Archive, ArchiveRestore, Trash2, Pin, Eye, Users, UserPlus,
  X, Search, Lock, Loader2, CalendarClock, Save,
} from 'lucide-react';
import { useAnnouncements } from '../../context/AnnouncementContext';
import { useAuth } from '../../context/AuthContext';
import * as api from '../../services/announcementService';
import { LEVELS, levelOf, formatDateTime } from '../../components/announcements/announcementLevels';
import { BannerPill } from '../../components/announcements/AnnouncementBanner';
import '../../components/announcements/announcements.css';

const STATUS_STYLES = {
  live: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/40',
  scheduled: 'bg-sky-500/15 text-sky-300 border-sky-500/40',
  expired: 'bg-slate-500/15 text-slate-300 border-slate-500/40',
  archived: 'bg-slate-700/50 text-slate-400 border-slate-600',
};

const EMPTY_FORM = {
  title: '',
  body: '',
  level: 'update',
  link_url: '',
  link_label: '',
  is_pinned: false,
  starts_at: '',
  expires_at: '',
  renotify: false,
};

// <input type="datetime-local"> works in local time; the API wants ISO/UTC.
const isoToLocalInput = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};
const localInputToIso = (value) => (value ? new Date(value).toISOString() : null);

const inputCls = `w-full px-3 py-2 text-sm bg-slate-900/60 border border-slate-600 rounded-lg text-slate-100
  placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500`;

const errMsg = (err) => err?.message || 'Something went wrong';

// ---------------------------------------------------------------------------
// Editor
// ---------------------------------------------------------------------------

const Editor = ({ initial, onCancel, onSaved }) => {
  const isEdit = !!initial?.id;
  const [form, setForm] = useState(() => (isEdit
    ? {
      ...EMPTY_FORM,
      title: initial.title || '',
      body: initial.body || '',
      level: initial.level,
      link_url: initial.link_url || '',
      link_label: initial.link_label || '',
      is_pinned: !!initial.is_pinned,
      starts_at: isoToLocalInput(initial.starts_at),
      expires_at: isoToLocalInput(initial.expires_at),
    }
    : EMPTY_FORM));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const set = (field) => (e) => {
    const value = e?.target ? (e.target.type === 'checkbox' ? e.target.checked : e.target.value) : e;
    setForm((f) => ({ ...f, [field]: value }));
  };

  const setExpiryIn = (hours) => {
    const base = form.starts_at ? new Date(form.starts_at) : new Date();
    setForm((f) => ({ ...f, expires_at: hours ? isoToLocalInput(new Date(base.getTime() + hours * 3600e3).toISOString()) : '' }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    const payload = {
      ...form,
      starts_at: localInputToIso(form.starts_at),
      expires_at: localInputToIso(form.expires_at),
    };
    try {
      if (isEdit) await api.updateAnnouncement(initial.id, payload);
      else await api.createAnnouncement(payload);
      onSaved(isEdit ? 'Announcement updated' : 'Announcement posted');
    } catch (err) {
      setError(errMsg(err));
      setSaving(false);
    }
  };

  const previewItem = {
    id: 'preview',
    title: form.title || 'Your announcement title',
    body: form.body,
    level: form.level,
  };

  return (
    <form onSubmit={submit} className="bg-slate-800 border border-slate-700 rounded-lg shadow-sm">
      <div className="p-4 border-b border-slate-700 flex items-center">
        <h3 className="text-lg font-semibold text-slate-200 flex-1">
          {isEdit ? 'Edit announcement' : 'New announcement'}
        </h3>
        <button type="button" onClick={onCancel} className="p-1.5 rounded-md text-slate-400 hover:text-slate-100 hover:bg-slate-700">
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Live preview */}
      <div className="px-4 pt-4">
        <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Live preview</p>
        <div className="rounded-lg bg-slate-800 border border-slate-700 px-4 py-3 flex items-center gap-4">
          <span className="hidden md:inline text-base font-semibold text-slate-200 whitespace-nowrap">KSP | Headquarters</span>
          <div className="flex-1 min-w-0 flex justify-center">
            <BannerPill key={form.level} item={previewItem} isUnseen />
          </div>
        </div>
      </div>

      <div className="p-4 grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div className="space-y-4">
          <div>
            <label className="flex justify-between text-sm font-medium text-slate-300 mb-1">
              <span>Title *</span>
              <span className={`text-xs ${form.title.length > 150 ? 'text-red-400' : 'text-slate-500'}`}>{form.title.length}/150</span>
            </label>
            <input className={inputCls} value={form.title} onChange={set('title')} maxLength={150} required
              placeholder="e.g. Dock 4 closed for maintenance Friday" />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-300 mb-1">Type</label>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {Object.entries(LEVELS).map(([key, lvl]) => {
                const active = form.level === key;
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => set('level')(key)}
                    className={`flex flex-col items-center gap-1.5 px-2 py-3 rounded-lg border text-xs font-semibold transition
                      ${active ? `bg-gradient-to-br ${lvl.hero} border-white/40 text-white shadow-lg` : 'border-slate-600 text-slate-300 hover:bg-slate-700/60'}`}
                  >
                    <lvl.Icon className="w-5 h-5" />
                    {lvl.label}
                  </button>
                );
              })}
            </div>
            <p className="mt-1.5 text-xs text-slate-500">
              {form.level === 'urgent'
                ? 'Urgent pops open by itself for everyone who hasn’t seen it yet.'
                : form.level === 'celebration'
                  ? 'Celebrations throw confetti when opened. 🎉'
                  : 'Stays in the banner until it expires or you archive it.'}
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-300 mb-1">Details</label>
            <textarea className={`${inputCls} min-h-[160px]`} value={form.body} onChange={set('body')}
              placeholder="Shown in the popup. Line breaks are kept and links become clickable." />
          </div>
        </div>

        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1">Button link</label>
              <input className={inputCls} value={form.link_url} onChange={set('link_url')} placeholder="https://… or /tiles/…" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1">Button text</label>
              <input className={inputCls} value={form.link_label} onChange={set('link_label')} maxLength={80}
                placeholder="Learn more" disabled={!form.link_url} />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-300 mb-1">Start showing</label>
            <input type="datetime-local" className={`${inputCls} [color-scheme:dark]`} value={form.starts_at} onChange={set('starts_at')} />
            <p className="mt-1 text-xs text-slate-500">Leave empty to post right away.</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-300 mb-1">Stop showing</label>
            <input type="datetime-local" className={`${inputCls} [color-scheme:dark]`} value={form.expires_at} onChange={set('expires_at')} />
            <div className="mt-1.5 flex flex-wrap gap-1.5">
              {[['1 day', 24], ['3 days', 72], ['1 week', 168], ['2 weeks', 336], ['Never', 0]].map(([label, h]) => (
                <button key={label} type="button" onClick={() => setExpiryIn(h)}
                  className="px-2 py-0.5 rounded-full text-xs border border-slate-600 text-slate-300 hover:bg-slate-700">
                  {label}
                </button>
              ))}
            </div>
          </div>

          <label className="flex items-start gap-3 p-3 rounded-lg border border-slate-700 bg-slate-900/40 cursor-pointer">
            <input type="checkbox" className="mt-0.5 accent-blue-500" checked={form.is_pinned} onChange={set('is_pinned')} />
            <span>
              <span className="flex items-center gap-1.5 text-sm font-medium text-slate-200"><Pin className="w-3.5 h-3.5" /> Pin</span>
              <span className="block text-xs text-slate-400">Shown ahead of other announcements of the same type.</span>
            </span>
          </label>

          {isEdit && (
            <label className="flex items-start gap-3 p-3 rounded-lg border border-amber-500/40 bg-amber-500/5 cursor-pointer">
              <input type="checkbox" className="mt-0.5 accent-amber-500" checked={form.renotify} onChange={set('renotify')} />
              <span>
                <span className="block text-sm font-medium text-amber-200">Show it to everyone again</span>
                <span className="block text-xs text-slate-400">Resets who’s seen it, so it rotates back into everyone’s banner.</span>
              </span>
            </label>
          )}
        </div>
      </div>

      <div className="px-4 py-3 border-t border-slate-700 flex items-center justify-end gap-2">
        {error && <p className="mr-auto text-sm text-red-400">{error}</p>}
        <button type="button" onClick={onCancel} className="px-4 py-2 rounded-lg text-sm text-slate-300 hover:bg-slate-700">Cancel</button>
        <button type="submit" disabled={saving || !form.title.trim()}
          className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : isEdit ? <Save className="w-4 h-4" /> : <Megaphone className="w-4 h-4" />}
          {isEdit ? 'Save changes' : 'Post announcement'}
        </button>
      </div>
    </form>
  );
};

// ---------------------------------------------------------------------------
// Announcements tab
// ---------------------------------------------------------------------------

const AnnouncementsTab = ({ flash }) => {
  const [data, setData] = useState({ announcements: [], recentUserCount: 0, recentLoginDays: 14 });
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | {} (new) | announcement
  const [filter, setFilter] = useState('current');
  const { openModal, refresh: refreshFeed } = useAnnouncements();

  const load = useCallback(async () => {
    try {
      setData(await api.listAllAnnouncements());
    } catch (err) {
      flash(errMsg(err), true);
    } finally {
      setLoading(false);
    }
  }, [flash]);

  useEffect(() => { load(); }, [load]);

  const act = async (fn, message) => {
    try {
      await fn();
      flash(message);
      load();
      refreshFeed();
    } catch (err) {
      flash(errMsg(err), true);
    }
  };

  const shown = useMemo(() => data.announcements.filter((a) => {
    if (filter === 'current') return a.status === 'live' || a.status === 'scheduled';
    if (filter === 'past') return a.status === 'expired' || a.status === 'archived';
    return true;
  }), [data.announcements, filter]);

  const counts = useMemo(() => data.announcements.reduce((acc, a) => {
    acc[a.status] = (acc[a.status] || 0) + 1;
    return acc;
  }, {}), [data.announcements]);

  if (editing) {
    return (
      <Editor
        initial={editing}
        onCancel={() => setEditing(null)}
        onSaved={(message) => {
          setEditing(null);
          flash(message);
          load();
          refreshFeed();
        }}
      />
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex rounded-lg border border-slate-700 overflow-hidden text-sm">
          {[['current', `Live & scheduled (${(counts.live || 0) + (counts.scheduled || 0)})`],
            ['past', `Expired & archived (${(counts.expired || 0) + (counts.archived || 0)})`],
            ['all', 'All']].map(([key, label]) => (
            <button key={key} type="button" onClick={() => setFilter(key)}
              className={`px-3 py-1.5 ${filter === key ? 'bg-slate-700 text-white' : 'text-slate-400 hover:text-slate-200 hover:bg-slate-700/50'}`}>
              {label}
            </button>
          ))}
        </div>
        <button type="button" onClick={() => setEditing({})}
          className="ml-auto flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold bg-blue-600 hover:bg-blue-700 text-white">
          <Plus className="w-4 h-4" /> New announcement
        </button>
      </div>

      {loading ? (
        <div className="py-12 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
      ) : shown.length === 0 ? (
        <div className="bg-slate-800 border border-slate-700 rounded-lg p-10 text-center">
          <Megaphone className="w-10 h-10 text-slate-600 mx-auto mb-3" />
          <p className="text-slate-300">Nothing here yet.</p>
          {filter === 'current' && <p className="text-sm text-slate-500 mt-1">Post one and it shows up in everyone’s header instantly.</p>}
        </div>
      ) : (
        <div className="space-y-2">
          {shown.map((a) => {
            const lvl = levelOf(a.level);
            // Out of users who've logged in within the last recentLoginDays days.
            const pct = data.recentUserCount ? Math.min(100, Math.round((a.viewed_recent_count / data.recentUserCount) * 100)) : 0;
            return (
              <div key={a.id} className={`bg-slate-800 border border-slate-700 rounded-lg p-4 flex flex-col md:flex-row md:items-center gap-4
                ${a.status === 'archived' ? 'opacity-60' : ''}`}>
                <div className="flex items-start gap-3 min-w-0 flex-1">
                  <span className={`flex-shrink-0 w-10 h-10 rounded-xl flex items-center justify-center bg-gradient-to-br ${lvl.hero} text-white`}>
                    <lvl.Icon className="w-5 h-5" />
                  </span>
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-semibold text-slate-100 truncate">{a.title}</span>
                      {a.is_pinned && <Pin className="w-3.5 h-3.5 text-slate-400" />}
                      <span className={`text-[11px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full border ${STATUS_STYLES[a.status]}`}>
                        {a.status}
                      </span>
                      <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full border ${lvl.chip}`}>{lvl.label}</span>
                    </div>
                    <p className="text-xs text-slate-400 mt-1 flex flex-wrap gap-x-3 gap-y-0.5">
                      <span>{a.created_by_name || 'Unknown'} · {formatDateTime(a.created_at)}</span>
                      {a.starts_at && <span className="flex items-center gap-1"><CalendarClock className="w-3 h-3" /> starts {formatDateTime(a.starts_at)}</span>}
                      {a.expires_at && <span>ends {formatDateTime(a.expires_at)}</span>}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-4 flex-shrink-0">
                  <div className="w-36" title={`${a.viewed_recent_count} of ${data.recentUserCount} users who logged in within the last ${data.recentLoginDays} days opened it (${a.viewed_count} total)`}>
                    <div className="flex justify-between text-xs text-slate-400 mb-1">
                      <span className="flex items-center gap-1"><Eye className="w-3 h-3" /> {a.viewed_count} seen</span>
                      <span>{pct}%</span>
                    </div>
                    <div className="h-1.5 rounded-full bg-slate-700 overflow-hidden">
                      <div className={`h-full ${lvl.bar}`} style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    {a.status === 'live' && (
                      <button type="button" title="Preview popup" onClick={() => openModal(a.id)}
                        className="p-2 rounded-md text-slate-400 hover:text-slate-100 hover:bg-slate-700"><Eye className="w-4 h-4" /></button>
                    )}
                    <button type="button" title="Edit" onClick={() => setEditing(a)}
                      className="p-2 rounded-md text-slate-400 hover:text-slate-100 hover:bg-slate-700"><Pencil className="w-4 h-4" /></button>
                    {a.status === 'archived' ? (
                      <button type="button" title="Restore" onClick={() => act(() => api.unarchiveAnnouncement(a.id), 'Announcement restored')}
                        className="p-2 rounded-md text-slate-400 hover:text-slate-100 hover:bg-slate-700"><ArchiveRestore className="w-4 h-4" /></button>
                    ) : (
                      <button type="button" title="Archive (hide from everyone)" onClick={() => act(() => api.archiveAnnouncement(a.id), 'Announcement archived')}
                        className="p-2 rounded-md text-slate-400 hover:text-slate-100 hover:bg-slate-700"><Archive className="w-4 h-4" /></button>
                    )}
                    <button type="button" title="Delete permanently"
                      onClick={() => {
                        if (window.confirm(`Delete "${a.title}" permanently? This can't be undone. (Archive hides it instead.)`)) {
                          act(() => api.deleteAnnouncement(a.id), 'Announcement deleted');
                        }
                      }}
                      className="p-2 rounded-md text-slate-400 hover:text-red-300 hover:bg-red-500/10"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Admins tab
// ---------------------------------------------------------------------------

const AdminsTab = ({ flash }) => {
  const { currentUser } = useAuth();
  const { refresh: refreshFeed } = useAnnouncements();
  const [admins, setAdmins] = useState([]);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);

  const load = useCallback(async () => {
    try {
      const data = await api.listAdmins();
      setAdmins(data.admins || []);
    } catch (err) {
      flash(errMsg(err), true);
    }
  }, [flash]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const q = query.trim();
    if (q.length < 2) {
      setResults([]);
      return undefined;
    }
    setSearching(true);
    const t = setTimeout(async () => {
      try {
        const data = await api.searchUsers(q);
        setResults(data.users || []);
      } catch (err) {
        flash(errMsg(err), true);
      } finally {
        setSearching(false);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [query, flash, admins]);

  const add = async (user) => {
    try {
      await api.addAdmin(user.id);
      flash(`${user.full_name || user.username} can now manage announcements`);
      setQuery('');
      load();
    } catch (err) {
      flash(errMsg(err), true);
    }
  };

  const remove = async (admin) => {
    const self = admin.user_id === currentUser?.id;
    const msg = self
      ? 'Remove YOURSELF as an announcement admin? You will lose access to this page.'
      : `Remove ${admin.full_name || admin.username} as an announcement admin?`;
    if (!window.confirm(msg)) return;
    try {
      await api.removeAdmin(admin.user_id);
      flash('Admin removed');
      if (self) refreshFeed();
      else load();
    } catch (err) {
      flash(errMsg(err), true);
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <div className="bg-slate-800 border border-slate-700 rounded-lg">
        <div className="p-4 border-b border-slate-700">
          <h3 className="font-semibold text-slate-200 flex items-center gap-2"><Users className="w-4 h-4" /> Announcement admins ({admins.length})</h3>
          <p className="text-xs text-slate-400 mt-1">Every admin has full control, including adding and removing other admins.</p>
        </div>
        <ul className="divide-y divide-slate-700">
          {admins.map((a) => (
            <li key={a.user_id} className="px-4 py-3 flex items-center gap-3">
              <span className="w-9 h-9 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-sm font-bold text-white">
                {(a.full_name || a.username || '?').charAt(0).toUpperCase()}
              </span>
              <div className="min-w-0 flex-1">
                <p className="text-sm text-slate-100 truncate">
                  {a.full_name || a.username}
                  {a.user_id === currentUser?.id && <span className="ml-2 text-xs text-slate-400">(you)</span>}
                </p>
                <p className="text-xs text-slate-400 truncate">
                  {a.username} · added {formatDateTime(a.added_at)}{a.added_by_name ? ` by ${a.added_by_name}` : ''}
                </p>
              </div>
              <button type="button" onClick={() => remove(a)} disabled={admins.length <= 1}
                title={admins.length <= 1 ? 'There must always be at least one admin' : 'Remove admin'}
                className="p-2 rounded-md text-slate-400 hover:text-red-300 hover:bg-red-500/10 disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-slate-400">
                <Trash2 className="w-4 h-4" />
              </button>
            </li>
          ))}
        </ul>
      </div>

      <div className="bg-slate-800 border border-slate-700 rounded-lg">
        <div className="p-4 border-b border-slate-700">
          <h3 className="font-semibold text-slate-200 flex items-center gap-2"><UserPlus className="w-4 h-4" /> Add an admin</h3>
        </div>
        <div className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input className={`${inputCls} pl-9`} value={query} onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by name, username or email" />
            {searching && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-slate-400" />}
          </div>
          <ul className="mt-3 space-y-1">
            {results.map((u) => (
              <li key={u.id} className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-slate-700/40">
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-slate-100 truncate">{u.full_name || u.username}</p>
                  <p className="text-xs text-slate-400 truncate">{u.username}{u.email ? ` · ${u.email}` : ''}</p>
                </div>
                <button type="button" onClick={() => add(u)}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-blue-600 hover:bg-blue-700 text-white">
                  <Plus className="w-3.5 h-3.5" /> Add
                </button>
              </li>
            ))}
            {query.trim().length >= 2 && !searching && results.length === 0 && (
              <li className="px-3 py-2 text-sm text-slate-500">No matching users (existing admins are hidden).</li>
            )}
          </ul>
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

const ManageAnnouncements = () => {
  const { enabled, loaded, isAdmin } = useAnnouncements();
  const [tab, setTab] = useState('announcements');
  const [toast, setToast] = useState(null);

  const flash = useCallback((message, isError = false) => {
    setToast({ message, isError, key: Date.now() });
  }, []);

  useEffect(() => {
    if (!toast) return undefined;
    const t = setTimeout(() => setToast(null), 3500);
    return () => clearTimeout(t);
  }, [toast]);

  if (!loaded && enabled) {
    return <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;
  }

  if (!isAdmin) {
    return (
      <div className="bg-slate-800 border border-slate-700 rounded-lg shadow-sm p-8 text-center">
        <Lock className="w-16 h-16 text-red-500 mx-auto mb-4" />
        <h2 className="text-xl font-semibold text-slate-200 mb-2">Permission Denied</h2>
        <p className="text-slate-400">Only announcement admins can manage announcements.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-6">
      <div className="bg-slate-800 border border-slate-700 rounded-lg shadow-sm p-6">
        <div className="flex items-center">
          <Megaphone className="w-6 h-6 text-blue-400 mr-3" />
          <h2 className="text-xl font-semibold text-slate-200">Announcements</h2>
        </div>
        <p className="text-slate-400 mt-2">Post announcements to the banner in everyone’s header. Changes show up live for everyone.</p>
        <div className="mt-4 flex gap-1 border-b border-slate-700 -mb-6 -mx-6 px-6">
          {[['announcements', 'Announcements', Megaphone], ['admins', 'Admins', Users]].map(([key, label, Icon]) => (
            <button key={key} type="button" onClick={() => setTab(key)}
              className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors
                ${tab === key ? 'border-blue-500 text-white' : 'border-transparent text-slate-400 hover:text-slate-200'}`}>
              <Icon className="w-4 h-4" /> {label}
            </button>
          ))}
        </div>
      </div>

      {tab === 'announcements' ? <AnnouncementsTab flash={flash} /> : <AdminsTab flash={flash} />}

      {toast && (
        <div key={toast.key}
          className={`ksp-ann-fade-up fixed bottom-6 right-6 z-50 px-4 py-3 rounded-lg shadow-xl border text-sm
            ${toast.isError ? 'bg-red-900/90 border-red-700 text-red-100' : 'bg-emerald-900/90 border-emerald-700 text-emerald-100'}`}>
          {toast.message}
        </div>
      )}
    </div>
  );
};

export default ManageAnnouncements;
